import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { _ } from '/@/locale';
import { isMobile } from '/@/utils';
// V3 Components
import Dots from '/@/v3/components/dots';
import { NewDotsReference } from '/@/v3/components/new-dots-reference';
import { RangeReference } from '/@/v3/components/range-reference';
import styles from './styles.module.css';

@autobind
export default class Metrics extends Component {
    static propTypes = {
        dataKey: PropTypes.string.isRequired,
        products: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                product: PropTypes.shape({
                    name: PropTypes.string.isRequired,
                }),
                metrics: PropTypes.arrayOf(
                    PropTypes.shape({
                        name: PropTypes.object.isRequired,
                        value: PropTypes.number.isRequired,
                        referenceCodename: PropTypes.string.isRequired,
                    })
                ),
                features: PropTypes.arrayOf(
                    PropTypes.shape({
                        name: PropTypes.object.isRequired,
                        value: PropTypes.object.isRequired,
                    })
                ),
            })
        ).isRequired,
        dots: PropTypes.bool,
        brand: PropTypes.object,
        highlight: PropTypes.bool,
        country: PropTypes.any,
        filterMetrics: PropTypes.array,
    };

    groupProducts(products) {
        const productGroups = products.reduce((groups, product) => {
            product[this.props.dataKey]
                .filter((m1) => {
                    if (!this.props.filterMetrics) return true;
                    return this.props.filterMetrics.some((m2) => m1.id === m2);
                })
                .forEach(({ name, value, order }) => {
                    name = _(name);
                    (groups[name] = groups[name] || []).push({
                        id: product.id,
                        name: product.label ? product.label : product.catalogName,
                        ranking: product.ranking,
                        value,
                        order,
                    });
                });
            return groups;
        }, {});
        return Object.entries(productGroups)
            .sort((a, b) => a[1]?.[0]?.order - b[1]?.[0]?.order)
            .map(([name, products]) => ({ name, products }));
    }

    getGroupComponent({ name, products }) {
        return (
            <div key={name} className={styles.group}>
                <div className={styles.title}>{name}</div>
                <div className={styles.list}>
                    {products.map((product) => (
                        <div key={product.id} className={styles.row}>
                            <div className={styles.product}>{product.catalogName}</div>
                            {this.renderValue(product.value)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    getTableComponent({ columns, rows, data }) {
        return (
            <table cellSpacing={0}>
                <thead>
                    <tr>
                        <th className={styles.border} />
                        {columns.map((title) => (
                            <th key={title} className={styles.border}>
                                {title}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(({ name, isDisease }) => {
                        return (
                            <tr key={name}>
                                <td className={classNames(styles.border, isDisease ? styles.disease : '')}>{name}</td>
                                {columns.map((column) => (
                                    <td key={name + Math.random()} className={styles.border}>
                                        {!!data[name] && !!data[name][column] && this.renderValue(data[name][column])}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    renderValue(value) {
        if (this.props.dots) {
            return (
                <Dots
                    max={this.props.brand.metricsMaxValue}
                    value={value}
                    theme={this.props.brand.theme}
                    showValues={this.props.brand.showMetricsValues}
                    monochrome={this.props.brand.showMetricsUsingBrandTheme}
                />
            );
        }
        return <div className={styles.value}>{_(value)}</div>;
    }

    isDisease(groups, products) {
        const metrics = {};
        products.forEach((product) => {
            product.metrics.forEach((metric) => {
                if (!metrics[metric.name.es]) metrics[metric.name.es] = metric.isDisease;
            });
        });
        return groups.map((group) => {
            return { ...group, isDisease: metrics[group.name] };
        });
    }

    render() {
        const products = this.props.products.filter(
            (product) => product[this.props.dataKey] && product[this.props.dataKey].length > 0
        );

        if (products.length === 0) return null;

        let groups = this.groupProducts(products);
        if (this.props.dataKey === 'metrics') groups = this.isDisease(groups, products);
        const single = products.length === 1;

        const Group = this.getGroupComponent;
        const Table = this.getTableComponent;
        const mobile = isMobile();

        const referenceCodenames = [];
        if (this.props.dots) {
            products.forEach((product) =>
                product.metrics.forEach(
                    (metric) =>
                        metric.referenceCodename !== 'default' &&
                        !referenceCodenames.includes(metric.referenceCodename) &&
                        referenceCodenames.push(metric.referenceCodename)
                )
            );
        }
        const useDefaultReference = referenceCodenames.length === 0;

        return (
            <div className={styles.root}>
                {this.props.dots && (
                    <div className={styles.reference}>
                        {useDefaultReference ? (
                            <NewDotsReference brand={this.props.brand} country={this.props.country} />
                        ) : (
                            <RangeReference
                                hideTitle
                                maxWidth={false}
                                referenceCodenames={referenceCodenames}
                                theme={this.props.brand.theme}
                            />
                        )}
                    </div>
                )}
                {mobile ? (
                    <div className={styles.groups}>
                        {groups.map((group) => (
                            <Group key={group.name} name={group.name} products={group.products} />
                        ))}
                    </div>
                ) : (
                    <div
                        className={classNames(styles.table, {
                            [styles.single]: single,
                            [styles.highlight]: this.props.highlight && !single,
                        })}
                    >
                        <Table
                            columns={products.map((x) => (x.label ? x.label : x.catalogName))}
                            rows={groups.map((x) => {
                                return { name: x.name, isDisease: x.isDisease };
                            })}
                            data={groups.reduce(
                                (acc, x) => ({
                                    ...acc,
                                    [x.name]: x.products.reduce(
                                        (acc, { name, value }) => ({
                                            ...acc,
                                            [name]: value,
                                        }),
                                        {}
                                    ),
                                }),
                                {}
                            )}
                        />
                    </div>
                )}
            </div>
        );
    }
}
